import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, Filter, Download, Eye, Phone, Mail, RotateCcw, Trash2, X } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// RATING_CARDS_PAGOS_MES_V1
const etapaRating = lead => String(lead.stage || lead.status || 'ABERTURA').toUpperCase();
const comerciaisRating = [
    'ABERTURA',
    'NOVO_LEAD',
    'EM_DIAGNOSTICO',
    'EMPATIA_ENVIADA',
    'EDUCACAO_ATIVA',
    'AUTORIDADE_APRESENTADA',
    'EM_QUALIFICACAO',
    'QUALIFICADO_PF',
    'QUALIFICADO_PJ',
    'QUALIFICADO_DUPLO',
    'OFERTA_PF',
    'OFERTA_PJ',
    'AGUARDANDO_FORMA_PAGAMENTO',
    'OBJECAO_ATIVA'
  ];
const correspondeCardRating = (lead, filtro) => {
  const etapa = etapaRating(lead);
  if (filtro === 'todos') return true;
  if (filtro === 'card_atendimento') return comerciaisRating.includes(etapa);
  if (filtro === 'card_documentacao') return lead.documentacao_completa === true;
  if (filtro === 'card_producao') return ['EM_PRODUCAO','PRODUCAO','EM_EXECUCAO'].includes(etapa);
  if (filtro === 'card_finalizados') return ['FINALIZADOS','FINALIZADO','CONCLUIDO'].includes(etapa);
  if (filtro === 'card_pagos_mes') return lead.pago_no_mes === true;
  const sf = filtro.toLowerCase();
  return (lead.stage || '').toLowerCase() === sf || (lead.status || '').toLowerCase() === sf;
};

const Leads = () => {
  const { darkMode } = useSettings();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [reiniciandoLead, setReiniciandoLead] = useState(null);

  /*
   * LEADS_MANUAL_DELETE_V3
   *
   * Exclusao manual somente depois
   * da revisao humana da conversa.
   */
  const [excluindoLead, setExcluindoLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  const filterLeads = useCallback(() => {
    let filtered = leads;

    if (statusFilter !== 'todos') {
      filtered = filtered.filter(lead => correspondeCardRating(lead, statusFilter));
    }

    if (searchTerm) {
      filtered = filtered.filter(lead =>
        (lead.nome || lead.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.cpf_cnpj || '').includes(searchTerm) ||
        (lead.telefone || lead.phone || '').includes(searchTerm)
      );
    }

    setFilteredLeads(filtered);
  }, [leads, searchTerm, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [filterLeads]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetLead = async (leadId, leadName) => {
    if (!window.confirm(`Reiniciar atendimento de "${leadName || 'sem nome'}"?\n\nIsso irá:\n• Apagar funil/comercial\n• Limpar contexto Redis\n• Manter dados pessoais\n• Reiniciar no estágio inicial`)) {
      return;
    }

    setReiniciandoLead(leadId);
    try {
      await axios.post(`${API_URL}/leads/${leadId}/reset`);
      alert(`Atendimento de "${leadName || 'sem nome'}" reiniciado com sucesso!`);
      fetchLeads(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao resetar lead:', error);
      alert('Erro ao resetar lead. Tente novamente.');
    } finally {
      setReiniciandoLead(null);
    }
  };

    const handleDeleteLead = async (lead) => {
    if (!lead || !lead.id) {
      return;
    }

    const identificacao =
      lead.nome ||
      lead.name ||
      lead.phone ||
      ('Lead #' + lead.id);

    const confirmado = window.confirm(
      'Excluir este lead?\n\n' +
      identificacao +
      '\n\n' +
      'Use esta opcao somente depois de revisar a conversa. ' +
      'Esta acao nao pode ser desfeita.'
    );

    if (!confirmado) {
      return;
    }

    try {
      setExcluindoLead(lead.id);

      /*
       * LEADS_DELETE_API_FIX_V1
       *
       * Leads.js usa axios + API_URL.
       */
      const resposta =
        await axios.delete(
          `${API_URL}/leads/${lead.id}`,
          {
            data: {
              phone: lead.phone || null
            }
          }
        );

      console.log(
        '[LEAD_MANUAL_DELETE_OK]',
        {
          leadId: lead.id,
          phone: lead.phone || null,
          resposta: resposta?.data || null
        }
      );

      setLeads(
        atuais =>
          atuais.filter(
            item => item.id !== lead.id
          )
      );

      if (
        selectedLead &&
        selectedLead.id === lead.id
      ) {
        setSelectedLead(null);
      }

      alert(
        'Lead excluido com sucesso.'
      );
    } catch (error) {
      console.error(
        '[LEAD_MANUAL_DELETE_ERROR]',
        error
      );

      alert(
        error?.response?.data?.error ||
        'Nao foi possivel excluir o lead.'
      );
    } finally {
      setExcluindoLead(null);
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/export/sheet`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar:', error);
    }
  };

  const getStatusColor = status => {
    const etapa = String(status || '')
      .toUpperCase();

    if ([
      'FINALIZADOS',
      'FINALIZADO',
      'CONCLUIDO'
    ].includes(etapa)) {
      return 'bg-emerald-100 text-emerald-800';
    }

    if ([
      'AGUARDANDO_PAGAMENTO',
      'AGUARDANDO_CONTRATO'
    ].includes(etapa)) {
      return 'bg-yellow-100 text-yellow-800';
    }

    if ([
      'PAGAMENTO_CONFIRMADO',
      'AGUARDANDO_DOCUMENTOS',
      'DOCUMENTACAO_PENDENTE',
      'DOCUMENTACAO_RECEBIDA'
    ].includes(etapa)) {
      return 'bg-orange-100 text-orange-800';
    }

    if ([
      'EM_PRODUCAO',
      'PRODUCAO',
      'EM_EXECUCAO'
    ].includes(etapa)) {
      return 'bg-cyan-100 text-cyan-800';
    }

    if (etapa === 'ATRASADOS') {
      return 'bg-red-100 text-red-800';
    }

    return 'bg-blue-100 text-blue-800';
  };

  const etapaDoLead = lead =>
    String(
      lead.stage ||
      lead.status ||
      'ABERTURA'
    ).toUpperCase();

  const etapasComerciais = [
    'ABERTURA',
    'NOVO_LEAD',
    'EM_DIAGNOSTICO',
    'EMPATIA_ENVIADA',
    'EDUCACAO_ATIVA',
    'AUTORIDADE_APRESENTADA',
    'EM_QUALIFICACAO',
    'QUALIFICADO_PF',
    'QUALIFICADO_PJ',
    'QUALIFICADO_DUPLO',
    'OFERTA_PF',
    'OFERTA_PJ',
    'AGUARDANDO_FORMA_PAGAMENTO',
    'OBJECAO_ATIVA'
  ];

  const etapasDocumentacao = [
    'PAGAMENTO_CONFIRMADO',
    'AGUARDANDO_DOCUMENTOS',
    'DOCUMENTACAO_PENDENTE',
    'DOCUMENTACAO_RECEBIDA'
  ];

  const statsCards = [
    {label:'Total',filter:'todos',color:'bg-blue-500'},
    {label:'Em atendimento',filter:'card_atendimento',color:'bg-indigo-500'},
    {label:'Aguardando contrato',filter:'aguardando_contrato',color:'bg-purple-500'},
    {label:'Aguardando pagamento',filter:'aguardando_pagamento',color:'bg-yellow-500'},
    {label:'Pagos no mês',filter:'card_pagos_mes',color:'bg-green-500'},
    {label:'Documentação concluída',filter:'card_documentacao',color:'bg-orange-500'},
    {label:'Em produção',filter:'card_producao',color:'bg-cyan-600'},
    {label:'Finalizados',filter:'card_finalizados',color:'bg-emerald-500'}
  ].map(card => ({...card,value:leads.filter(lead=>correspondeCardRating(lead,card.filter)).length}));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Leads</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Gerencie todos os leads do sistema</p>
        </div>
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download size={20} />
          Exportar Excel
        </button>
      </div>

      {/* Filters */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-4 flex flex-col md:flex-row gap-4`}>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
          >
            <option value="todos">Todas as etapas</option>
            <option value="card_atendimento">Em atendimento</option>
            <option value="card_pagos_mes">Pagos no mês</option>
            <option value="card_documentacao">Documentação concluída</option>
            <option value="card_producao">Em produção (todas)</option>
            <option value="card_finalizados">Finalizados (todos)</option>
            <option value="abertura">Abertura</option>
            <option value="em_diagnostico">Em diagnóstico</option>
            <option value="educacao_ativa">Educação ativa</option>
            <option value="em_qualificacao">Em qualificação</option>
            <option value="oferta_pf">Oferta PF</option>
            <option value="oferta_pj">Oferta PJ</option>
            <option value="aguardando_contrato">Aguardando contrato</option>
            <option value="aguardando_pagamento">Aguardando pagamento</option>
            <option value="aguardando_documentos">Aguardando documentos</option>
            <option value="documentacao_pendente">Documentação pendente</option>
            <option value="documentacao_recebida">Documentação recebida</option>
            <option value="em_producao">Em produção</option>
            <option value="finalizados">Finalizados</option>
            <option value="atrasados">Atrasados</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-4">
        {statsCards.map((stat, index) => (
          <button
            type="button"
            key={index}
            onClick={() => { setSearchTerm(''); setStatusFilter(stat.filter); }}
            aria-pressed={statusFilter === stat.filter}
            title={'Ver leads: ' + stat.label}
            className={
              `${
                darkMode
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              } border rounded-xl p-4 shadow-sm min-h-[142px] text-left cursor-pointer focus-visible:ring-4 focus-visible:ring-blue-300 ${statusFilter === stat.filter ? 'ring-2 ring-blue-400' : 'hover:shadow-lg'}`
            }
          >
            <div className="h-full flex flex-col">
              <div className={
                `${stat.color} w-11 h-11 rounded-lg flex items-center justify-center`
              }>
                <span className="text-white text-lg font-bold">
                  {stat.value}
                </span>
              </div>

              <p className={
                `${
                  darkMode
                    ? 'text-white'
                    : 'text-gray-900'
                } text-2xl font-bold mt-3 leading-none`
              }>
                {stat.value}
              </p>

              <p className={
                `${
                  darkMode
                    ? 'text-gray-300'
                    : 'text-gray-600'
                } text-sm leading-5 mt-2`
              }>
                {stat.label}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr className={`text-left text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <th className="px-6 py-3">Nome</th>
                <th className="px-6 py-3">CPF/CNPJ</th>
                <th className="px-6 py-3">Contato</th>
                <th className="px-6 py-3">Etapa atual</th>
                <th className="px-6 py-3">Tipo</th>
                <th className="px-6 py-3">Origem</th>
                <th className="px-6 py-3">Entrada</th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                  <td className={`px-6 py-4 font-medium ${darkMode ? 'text-white' : ''}`}>{lead.nome || lead.name || 'N/A'}</td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{lead.cpf_cnpj || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-sm">
                        <Phone size={14} className="text-gray-400" />
                        {lead.telefone || lead.phone}
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <Mail size={14} className="text-gray-400" />
                        {lead.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full text-[11px] font-medium ${getStatusColor(lead.stage || lead.status)}`}>
                      {String(
                        lead.stage ||
                        lead.status ||
                        '-'
                      ).replace(/_/g, ' ')}
                    </span>
                  </td>

                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {lead.tipo_pessoa || '—'}
                  </td>

                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {lead.source || 'Não informada'}
                  </td>

                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {lead.created_at || lead.data_criacao
                      ? new Date(
                          lead.created_at ||
                          lead.data_criacao
                        ).toLocaleDateString('pt-BR')
                      : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Visualizar dados coletados"
                      >
                        <Eye size={20} />
                      </button>
                      <button
                        onClick={() => handleResetLead(lead.id, lead.nome || lead.name)}
                        disabled={reiniciandoLead === lead.id}
                        title="Reiniciar atendimento desde o início"
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          reiniciandoLead === lead.id
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        <RotateCcw size={12} className={reiniciandoLead === lead.id ? 'animate-spin' : ''} />
                        {reiniciandoLead === lead.id ? 'Reiniciando...' : 'Reiniciar'}
                      </button>

                      <button
                        onClick={() => handleDeleteLead(lead)}
                        disabled={excluindoLead === lead.id}
                        title="Excluir lead depois de revisar a conversa"
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          excluindoLead === lead.id
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        <Trash2 size={12} />
                        {excluindoLead === lead.id ? 'Excluindo...' : 'Excluir'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de visualização dos dados coletados */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl shadow-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <div className={`flex items-center justify-between border-b px-6 py-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-lg font-bold">Dados coletados — {selectedLead.nome || selectedLead.name || 'Lead'}</h2>
              <button onClick={() => setSelectedLead(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div>
                  <span className="font-medium">Etapa atual:</span>{' '}
                  {String(
                    selectedLead.stage ||
                    selectedLead.status ||
                    '—'
                  ).replace(/_/g, ' ')}
                </div>

                <div>
                  <span className="font-medium">Tipo:</span>{' '}
                  {selectedLead.tipo_pessoa || '—'}
                </div>

                <div>
                  <span className="font-medium">Origem:</span>{' '}
                  {selectedLead.source || 'Não informada'}
                </div>

                <div>
                  <span className="font-medium">Entrada:</span>{' '}
                  {selectedLead.created_at ||
                  selectedLead.data_criacao
                    ? new Date(
                        selectedLead.created_at ||
                        selectedLead.data_criacao
                      ).toLocaleString('pt-BR')
                    : '—'}
                </div>
              </div>
              {(() => {
                const meta = (selectedLead.metadata && selectedLead.metadata.coleta_documentos) || {};
                const faltando = meta.faltando || [];
                const campos = Object.entries(meta).filter(([k]) => k !== 'faltando' && k !== 'finalizado_em');
                return (
                  <>
                    {faltando.length > 0 && (
                      <div className="rounded-lg bg-red-50 p-3 text-red-700">
                        <strong>⚠️ Faltando:</strong> {faltando.join(', ')}
                      </div>
                    )}
                    {campos.length === 0 ? (
                      <p className="text-gray-500">Nenhum dado coletado ainda.</p>
                    ) : (
                      <div className={`grid gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {campos.map(([k, v]) => (
                          <div key={k} className="flex justify-between border-b py-1">
                            <span className="font-medium capitalize">{k.replace(/_/g, ' ')}</span>
                            <span className="text-right">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
